import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _tagged_template_literal from "@swc/helpers/src/_tagged_template_literal.mjs";
function _templateObject() {
    var data = _tagged_template_literal([
        "\n  position: relative;\n\n  &:focus {\n    ",
        "\n  }\n\n  img {\n    width: 100%;\n    max-width: 100%;\n    cursor: pointer;\n  }\n"
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { initWistiaVideo, useWistiaVideoApi, useIntersectionObserver } from "@miro-site/core";
var KEYBOARD_KEYS = {
    SPACE: " ",
    ENTER: "Enter"
};
var WistiaImageWrapper = styled.div.withConfig({
    componentId: "sc-3c0a60e6-0"
})(_templateObject(), function(param) {
    var theme = param.theme;
    return theme.normalizeStyles(_object_spread({}, theme.focus.outlines.default));
});
var Video = function(param) {
    var thumbnail = param.thumbnail, thumbnailAlt = param.thumbnailAlt, rootRef = param.rootRef, id = param.id, containerId = param.containerId, controls = param.controls, togglePlay = param.togglePlay, time = param.time, options = param.options;
    var theme = useTheme();
    var ref = useState(!!thumbnail), isThumbnailVisible = ref[0], setIsThumbnailVisible = ref[1];
    var ref1 = useState(controls), isControlsVisible = ref1[0], setIsControlsVisible = ref1[1];
    var ref2 = useState(false), isInitialized = ref2[0], setIsInitialized = ref2[1];
    var videoId = id || containerId;
    var wistiaRef = useRef(null);
    var thumbnailRef = useRef(null);
    var ref3 = useState(null), containerRef = ref3[0], setContainerRef = ref3[1];
    var container = useIntersectionObserver(containerRef, {
        freezeOnceVisible: true,
        rootMargin: "300px 0px 300px 0px"
    });
    var isVisible = !!(container === null || container === void 0 ? void 0 : container.isIntersecting);
    useEffect(function() {
        if (controls !== isControlsVisible) {
            setIsControlsVisible(controls);
            setIsInitialized(false);
        }
    }, [
        controls
    ]);
    useEffect(function() {
        if (rootRef === null || rootRef === void 0 ? void 0 : rootRef.current) {
            setContainerRef(rootRef);
            return;
        }
        if (thumbnailRef === null || thumbnailRef === void 0 ? void 0 : thumbnailRef.current) {
            setContainerRef(thumbnailRef);
            return;
        }
        if (wistiaRef === null || wistiaRef === void 0 ? void 0 : wistiaRef.current) {
            setContainerRef(wistiaRef);
            return;
        }
    }, [
        rootRef,
        thumbnailRef,
        wistiaRef
    ]);
    useEffect(function() {
        if (wistiaRef.current) {
            wistiaRef.current.style.height = "";
            if (!isControlsVisible && !thumbnail) {
                wistiaRef.current.style.pointerEvents = "none";
            }
        }
    }, [
        wistiaRef,
        isControlsVisible,
        thumbnail
    ]);
    var videoApi = useWistiaVideoApi({
        id: videoId || "",
        jsonp: true,
        options: options
    });
    useEffect(function() {
        if (isVisible && !isInitialized && !isThumbnailVisible) {
            prepareVideo();
        }
    }, [
        isVisible,
        isInitialized,
        isThumbnailVisible
    ]);
    useEffect(function() {
        if (isVisible && !isThumbnailVisible && videoApi) {
            videoApi === null || videoApi === void 0 ? void 0 : videoApi.play();
        }
        return function() {
            videoApi === null || videoApi === void 0 ? void 0 : videoApi.remove();
        };
    }, [
        isVisible,
        isThumbnailVisible,
        videoApi
    ]);
    useEffect(function() {
        if (!videoApi || isThumbnailVisible) {
            return;
        }
        if (typeof time !== "undefined") {
            videoApi.time(time);
        }
        if (typeof togglePlay !== "undefined") {
            togglePlay ? videoApi.play() : videoApi.pause();
        }
    }, [
        togglePlay,
        time,
        videoApi,
        isThumbnailVisible
    ]);
    var prepareVideo = function() {
        var classes = [];
        if (!isControlsVisible) {
            classes.push("settingsControl=false", "controlsVisibleOnLoad=false", "volumeControl=false", "playButton=false", "playbar=false", "smallPlayButton=false", "qualityControl=false", "fullscreenButton=false", "playbackRateControl=false", "muted=true");
        }
        setIsInitialized(true);
        if (wistiaRef.current) {
            initWistiaVideo({
                wrapper: wistiaRef.current,
                embedId: containerId,
                autoPlay: true,
                classes: classes,
                videoId: videoId
            });
        }
    };
    var handleClickThumbnail = function() {
        // Prevent content from shifting while thumbnail disappears and video appears
        if (wistiaRef.current) {
            var ref;
            wistiaRef.current.style.height = "".concat((ref = thumbnailRef.current) === null || ref === void 0 ? void 0 : ref.offsetHeight, "px");
        }
        if (isThumbnailVisible) {
            prepareVideo();
        }
        setIsThumbnailVisible(false);
    };
    var handleKeyPress = function(event) {
        if (event.key == KEYBOARD_KEYS.ENTER || event.key == KEYBOARD_KEYS.SPACE) {
            event.preventDefault();
            handleClickThumbnail();
        }
    };
    var getThumbnail = function() {
        return /*#__PURE__*/ _jsx(WistiaImageWrapper, {
            ref: thumbnailRef,
            tabIndex: 0,
            role: "button",
            onClick: handleClickThumbnail,
            onKeyPress: handleKeyPress,
            children: /*#__PURE__*/ _jsxs("picture", {
                children: [
                    /*#__PURE__*/ _jsx("source", {
                        srcSet: "https://embed-fastly.wistia.com/deliveries/".concat(thumbnail, ".webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480"),
                        type: "image/webp",
                        media: theme.mediaQueries.v2Mobile
                    }),
                    /*#__PURE__*/ _jsx("source", {
                        srcSet: "https://embed-fastly.wistia.com/deliveries/".concat(thumbnail, ".jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480"),
                        type: "image/jpeg",
                        media: theme.mediaQueries.v2Mobile
                    }),
                    /*#__PURE__*/ _jsx("source", {
                        srcSet: "https://embed-fastly.wistia.com/deliveries/".concat(thumbnail, ".webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0"),
                        type: "image/webp"
                    }),
                    /*#__PURE__*/ _jsx("source", {
                        srcSet: "https://embed-fastly.wistia.com/deliveries/".concat(thumbnail, ".jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0"),
                        type: "image/jpeg"
                    }),
                    /*#__PURE__*/ _jsx("img", {
                        src: "https://embed-fastly.wistia.com/deliveries/".concat(thumbnail, ".jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0"),
                        alt: thumbnailAlt
                    })
                ]
            })
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isThumbnailVisible && getThumbnail(),
            /*#__PURE__*/ _jsx("div", {
                ref: wistiaRef,
                "data-testid": "wistia-element-ref"
            }, JSON.stringify({
                controls: controls
            }))
        ]
    });
};
export default Video;
